/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from "react";
import _ from "lodash";
import { Form, Accordion, Icon, Table, Popup, Dropdown, Input } from "semantic-ui-react";
import styles from "./EditorDefines.module.scss";

export type EditorDefinesProps = {
  editorDef: string | null;
  handleEditorDefChanged: (editorDef: string | null) => void;
  handleMinMaxItmOnUnitChange: (minValue: number, maxValue: number, itemCount: number) => void;
  permissions: boolean;
};

type EditorDefinesState = {
  isOpen: boolean;
};

type EditorDefine = {
  type: string;
  mask: string;
  decimal: number;
  unit: string;
};

// Mapping type value into text
const typeMapping = {
  "0": "Numeric",
  "1": "Boolean",
  "2": "String",
  "3": "Enumeration"
};

/** EditorDefines component */
class EditorDefines extends React.Component<EditorDefinesProps, EditorDefinesState> {
  state = { isOpen: false };

  render(): JSX.Element {
    const { editorDef, permissions } = this.props;
    const { isOpen } = this.state;
    const defines = this.decodeEditorDefines(editorDef ? editorDef : "");

    // Creates type dropdown options
    const typeOptions = _.keys(typeMapping).map(index => ({
      key: parseInt(index, 10),
      // TODO: fix types
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      text: typeMapping[index],
      value: index
    }));

    return (
      <Form.Group id="editorDefines" grouped={true}>
        <Form.Field>
          <Accordion fluid={true} styled={true}>
            <Accordion.Title
              active={isOpen}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onClick={() => this.setState(prevState => ({ isOpen: !isOpen }))}
            >
              <Icon name="dropdown" />
              <label>
                <b>Editor defines</b>
              </label>
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Table collapsing={true}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Mask</Table.HeaderCell>
                    <Table.HeaderCell>Decimal</Table.HeaderCell>
                    <Table.HeaderCell>Unit</Table.HeaderCell>
                    {!permissions && (
                      <Table.HeaderCell>
                        <Popup
                          trigger={
                            <Icon
                              id="addEditorDefault"
                              className="color-green"
                              link={true}
                              name="add"
                              onClick={() => this.handleAddEditorDefine(defines)}
                            />
                          }
                          content={"Add editor default"}
                        />
                      </Table.HeaderCell>
                    )}
                  </Table.Row>
                </Table.Header>
                {defines.length > 0 && (
                  <Table.Body>
                    {defines.map((define, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <Dropdown
                            id={`typeDropdown-${index}`}
                            size="mini"
                            compact={true}
                            fluid={true}
                            selection={true}
                            value={define.type}
                            options={typeOptions}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onChange={(e: any, data: any) =>
                              this.handleEditorDefineChange(
                                defines,
                                index,
                                this.handleTypeChanged(define, data.value)
                              )
                            }
                            disabled={permissions}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          {// TODO: fix types
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          typeMapping[define.type] === "Boolean" && (
                            <Input
                              id={`maskInput-${index}`}
                              className={styles.input}
                              value={define.mask}
                              size="mini"
                              // eslint-disable-next-line
                              onChange={({}, { value }: any) =>
                                this.handleEditorDefineChange(defines, index, {
                                  ...define,
                                  mask: value
                                })
                              }
                              disabled={permissions}
                            />
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {// TODO: fix types
                          // @ts-ignore
                          typeMapping[define.type] === "Numeric" && (
                            <Input
                              id={`decimalInput-${index}`}
                              className={styles.input}
                              type="number"
                              size="mini"
                              value={define.decimal}
                              // eslint-disable-next-line
                              onChange={({}, { value }: any) =>
                                this.handleEditorDefineChange(defines, index, {
                                  ...define,
                                  decimal: value
                                })
                              }
                              disabled={permissions}
                            />
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            id={`unitInput-${index}`}
                            className={styles.input}
                            size="mini"
                            value={
                              typeof define.unit === "string" &&
                              define.unit.toUpperCase().includes("ASCII")
                                ? "ASCII"
                                : define.unit
                            }
                            // eslint-disable-next-line
                            onChange={({}, { value }: any) =>
                              this.handleEditorDefineChange(defines, index, {
                                ...define,
                                unit:
                                  typeof value === "string" && value.toUpperCase().includes("ASCII")
                                    ? "ASCII"
                                    : value
                              })
                            }
                            disabled={permissions}
                          />
                        </Table.Cell>
                        {!permissions && (
                          <Table.Cell>
                            <Popup
                              trigger={
                                <Icon
                                  id={`remove-${index}`}
                                  className="color-red"
                                  link={true}
                                  name="remove"
                                  onClick={() => this.handleRemoveEditorDefine(defines, index)}
                                />
                              }
                              content={"Remove editor default"}
                            />
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ))}
                  </Table.Body>
                )}
              </Table>
              <Form.Field
                id="valueInDatabase"
                control={Input}
                label="Value in database"
                readOnly={true}
                value={editorDef ? editorDef : "null"}
              />
            </Accordion.Content>
          </Accordion>
        </Form.Field>
      </Form.Group>
    );
  }

  // Decode KCECPU EditorDef format into understandable format
  private decodeEditorDefines = (editorDefineStr: string): EditorDefine[] => {
    const defines: EditorDefine[] = [];
    let editorDefs = editorDefineStr ? editorDefineStr.split(";") : [];
    while (editorDefs.length >= 4) {
      defines.push({
        type: editorDefs[0],
        mask: editorDefs[1],
        decimal: editorDefs[2] === "" ? 0 : parseInt(editorDefs[2], 10),
        unit: editorDefs[3]
      });
      // remove 4 values from beginning of array
      editorDefs = editorDefs.splice(4);
    }
    return defines;
  };

  // Encode back to KCECPU EditorDef format
  private encodeEditorDefines = (editorDefines: EditorDefine[]): string => {
    const editorDefs: string[] = [];
    editorDefines.forEach(editorDefine => {
      editorDefs.push(editorDefine.type);
      editorDefs.push(editorDefine.mask);
      editorDefs.push(editorDefine.decimal === 0 ? "" : editorDefine.decimal.toString());
      editorDefs.push(editorDefine.unit);
    });
    return editorDefs.join(";");
  };

  // Type changed handler
  private handleTypeChanged = (define: EditorDefine, type: string): EditorDefine => {
    if (type !== define.type) {
      return {
        ...define,
        type,
        mask: "",
        decimal: 0
      };
    } else {
      return define;
    }
  };

  // Editor define changed handler
  private handleEditorDefineChange = (
    defines: EditorDefine[],
    index: number,
    changedDefine: EditorDefine
  ): void => {
    const previousDefines = this.encodeEditorDefines(defines);
    const { handleEditorDefChanged, handleMinMaxItmOnUnitChange } = this.props;
    defines[index] = changedDefine;
    const editorDefSrt = this.encodeEditorDefines(defines);

    if (previousDefines.includes("2;;;ASCII") && !editorDefSrt.includes("2;;;ASCII")) {
      handleMinMaxItmOnUnitChange(0, 0, 0);
    } else if (!previousDefines.includes("2;;;ASCII") && editorDefSrt.includes("2;;;ASCII")) {
      //reset min, max, Item count and entered string
      handleMinMaxItmOnUnitChange(0, 0, 0);
    }
    handleEditorDefChanged(editorDefSrt);
  };

  // Add new editor define handler
  private handleAddEditorDefine = (defines: EditorDefine[]): void => {
    const { handleEditorDefChanged } = this.props;
    const newDefine: EditorDefine = {
      type: "0",
      mask: "",
      decimal: 0,
      unit: ""
    };
    const editorDefSrt = this.encodeEditorDefines([...defines, newDefine]);
    handleEditorDefChanged(editorDefSrt);
  };

  // Remove editor define handler
  private handleRemoveEditorDefine = (defines: EditorDefine[], index: number): void => {
    const definesBeforeRemoval = this.encodeEditorDefines(defines);
    const { handleEditorDefChanged, handleMinMaxItmOnUnitChange } = this.props;
    defines.splice(index, 1);
    const editorDefSrt = this.encodeEditorDefines(defines);
    if (definesBeforeRemoval.includes("2;;;ASCII") && !editorDefSrt.includes("2;;;ASCII")) {
      //reset min, max, Item count and entered string
      handleMinMaxItmOnUnitChange(0, 0, 0);
    }
    if (editorDefSrt.length > 0) {
      handleEditorDefChanged(editorDefSrt);
    } else {
      handleEditorDefChanged(null);
    }
  };
}

export default EditorDefines;
